import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import LazyLoad from 'react-lazy-load';
import throttle from 'lodash/throttle';
import Head from 'next/head'
import { useIsomorphicLayoutEffect } from '../../../adapters/helpers/Hooks';
import { roundUpTheSize } from './BackgroundImage.helpers'
import ImageFactory from '../../../adapters/cloudinary/ImageFactory';
import { mediaQueryHOC } from '../../../adapters/helpers/Hooks'
import { ImageConstants } from '../../../adapters/helpers/Constants';
import Image from 'next/image';
import OptimizedImage from '../../../utils/helpers/OptimizedImage';

function BackgroundImage(props) {
    const mobileBackgroundImageId = props?.mobileBackgroundImage?.asset?.fields?.file?.url || props.contentImageId;
    const desktopBackgroundImageId = props?.desktopBackgroundImage?.asset?.fields?.file?.url || props.contentImageId;
    const { backgroundOffsetTop: marginTop, backgroundPosition, alt = null, children = null, customStyles = null, ariaHidden = false, fetchPriority = "high" } = props;
    const isPreload = props.isPreload ? props.isPreload : false;
    const isBannerImage = props.isBannerImage ? props.isBannerImage : false;
    const isLCP = props.isLCP ? props.isLCP : false;
    const nodeRef = useRef(null);
    const placeholderRef = useRef(null);
    const [width, setWidth] = useState(ImageConstants.defaultImageWindWidth);

    // Use each source's own aspect ratio so mobile/desktop divs get the correct height.
    // Fall back to the shared widthToHeightRatio when per-source data is absent.
    const mobileRatio   = props?.mobileBackgroundImage?.imageRendition?.fields?.aspectRatio  || props.widthToHeightRatio;
    const desktopRatio  = props?.desktopBackgroundImage?.imageRendition?.fields?.aspectRatio || props.widthToHeightRatio;
    // Lazy-load path shows desktop image when !isMedium, mobile image when isMedium.
    const lazyRatio     = props.isMedium ? mobileRatio : desktopRatio;

    const mobilePaddingTop  = `${100 / mobileRatio}%`;
    const desktopPaddingTop = `${100 / desktopRatio}%`;
    const paddingTop        = `${100 / lazyRatio}%`;

    // Freeze banner/LCP padding values on first paint to prevent post-hydration CLS.
    const bannerMobilePaddingTopRef  = useRef(mobilePaddingTop);
    const bannerDesktopPaddingTopRef = useRef(desktopPaddingTop);
    const stableMobilePaddingTop  = (isLCP || (isPreload && isBannerImage)) ? bannerMobilePaddingTopRef.current  : mobilePaddingTop;
    const stableDesktopPaddingTop = (isLCP || (isPreload && isBannerImage)) ? bannerDesktopPaddingTopRef.current : desktopPaddingTop;
    // Keep legacy single-value alias used by the lazy-load path.
    const stablePaddingTop = stableDesktopPaddingTop;
    // Initialise from props so the SSR value matches the first client render and
    // avoids a hydration-mismatch re-render.
    const [customClassName, setCustomClassName] = useState(props.customClassName || '');
    const mobileassertId = props?.preloadImage?.assetId ? props.preloadImage.assetId : props.imageId;
    function updateWidth() {
        const node = nodeRef.current || placeholderRef.current;
        if (node) {
            setWidth(roundUpTheSize(node.getBoundingClientRect().width));
        }
    }

    useEffect(
        () => {
            const handleResize = throttle(updateWidth, 100);
            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
                handleResize.cancel();
            };
        },
        []
    );

    useEffect(
        () => {
            setCustomClassName(props.customClassName);
        }, [props.customClassName]
    );

    useIsomorphicLayoutEffect(
        updateWidth,
        []
    );

    return (
        <React.Fragment>
            {(isPreload && isBannerImage) || isLCP ?
                <React.Fragment>
                    <Head>
                        <link rel="preload" href={ImageFactory.buildContentfullImageUrlByWidth(mobileBackgroundImageId, ImageConstants.defaultDesktopWidth)} as="image" media={`(max-width:${ImageConstants.mobile_max_width})`} fetchPriority='high' />
                        <link rel="preload" href={ImageFactory.buildContentfullImageUrlByWidth(desktopBackgroundImageId, ImageConstants.lcpDesktopWidth)} as="image" media={`(min-width:${ImageConstants.desktop_min_width})`} fetchPriority='high' />
                    </Head>
                    {/* Both mobile and desktop images are rendered in SSR HTML; CSS media queries control visibility. */}
                    <React.Fragment>
                        <div
                            className={`ob-background-wrapper ob-background-wrapper-mobile ${props.className ? props.className : ''} ${customClassName ? customClassName : ''}`}
                            aria-hidden={ariaHidden}
                            style={{
                                marginTop, paddingTop: stablePaddingTop, backgroundPosition,
                                position: 'relative',
                                ...customStyles
                            }}>
                            <OptimizedImage
                            src={ImageFactory.buildContentfullImageUrlByWidth(mobileBackgroundImageId, ImageConstants.defaultDesktopWidth)}
                            alt={alt || ''}
                            fill
                            style={{objectFit: 'cover'}}
                            isBanner
                            preload={false}
                            />
                            {children}
                        </div>
                        <div
                            className={`ob-background-wrapper ob-background-wrapper-desktop ${props.className ? props.className : ''} ${customClassName ? customClassName : ''}`}
                            ref={nodeRef}
                            aria-hidden={ariaHidden}
                            style={{
                                marginTop, paddingTop: stablePaddingTop, backgroundPosition,
                                position: 'relative',
                                ...customStyles
                            }}>
                           <OptimizedImage
                            src={ImageFactory.buildContentfullImageUrlByWidth(desktopBackgroundImageId, ImageConstants.lcpDesktopWidth)}
                            alt={alt || ''}
                            fill
                            style={{objectFit: 'cover'}}
                            isBanner
                            preload={false}
                            />
                            {children}
                        </div>
                    </React.Fragment>
                </React.Fragment>
                : <LazyLoad offset={500}
                    placeholder={
                        <div
                            className={`ob-background-wrapper ${props.className ? props.className : ''} ${customClassName ? customClassName : ''}`}
                            ref={placeholderRef}
                            aria-hidden={ariaHidden}
                            style={{
                                marginTop, paddingTop, backgroundPosition
                            }}
                        />
                    }
                >
                    <div
                        className={`ob-background-wrapper ${props.className ? props.className : ''} ${customClassName ? customClassName : ''}`}
                        ref={nodeRef}
                        aria-hidden={ariaHidden}
                        style={{
                            marginTop, paddingTop, backgroundPosition,
                            position: 'relative',
                            ...customStyles
                        }}>
                        <OptimizedImage
                            src={
                            (!props.isLarge && !props.isMedium) ||
                                (props.isLarge && !props.isMedium)
                                ? ImageFactory.buildContentfullImageUrl(
                                    desktopBackgroundImageId
                                )
                                : ImageFactory.buildContentfullImageUrl(
                                    mobileBackgroundImageId
                                )
                            }
                            alt={alt || ''}
                            fill
                            style={{objectFit: 'cover'}}
                            loading="eager"
                            fetchPriority="high"
                            className='ob-background-image-fill'
                        />
                        {children}
                    </div>
                </LazyLoad>}
        </React.Fragment>

    );
}

export default mediaQueryHOC(BackgroundImage);
BackgroundImage.defaultProps = {
    transformations: 'c_limit',
    fragments: [{ minWidth: 0, size: 1 }],
    widthToHeightRatio: 1
}

BackgroundImage.propTypes = {
    imageId: PropTypes.string,
    transformations: PropTypes.string,
    className: PropTypes.string,
    customClassName: PropTypes.string,
    fragments: PropTypes.array,
    widthToHeightRatio: PropTypes.number,
    children: PropTypes.node,
    backgroundOffsetTop: PropTypes.string,
    backgroundPosition: PropTypes.string,
    customStyles: PropTypes.object,
    format: PropTypes.any,
    isMedium: PropTypes.bool,
    ariaHidden: PropTypes.bool,
    alt: PropTypes.string,
    isPreload: PropTypes.bool,
    isBannerImage: PropTypes.bool,
    isLCP: PropTypes.bool,
    preloadImage: PropTypes.any,
    desktopBackgroundImage: PropTypes.any,
    mobileBackgroundImage: PropTypes.any,
    fetchPriority: PropTypes.oneOf(['high', 'low', 'auto'])
}